import type { TalentDiscoveryReqV3 } from '@revelio/data-access';

import { findFilterById } from '../../../filters/engine/filterRegistry';
import { type Filter, FilterId } from '../../../filters/types/filters';
import { buildCompanyFilter, buildMultiCompanyFilter } from './company';
import {
  buildKeywordsFilter,
  buildNameFilter,
  buildTitlesFilter,
} from './free-text-filters';
import { buildGeographyFilter, buildMultiGeographyFilter } from './geography';
import {
  buildEducationFilter,
  buildMultiEducationFilter,
  buildMultiEmploymentRelationshipFilter,
  buildMultiEthnicityFilter,
  buildMultiFlightRiskFilter,
  buildMultiGenderFilter,
  buildMultiPrestigeFilter,
  buildMultiRemoteSuitabilityFilter,
  buildMultiSeniorityFilter,
  buildMultiWorkScheduleFilter,
  buildSeniorityFilter,
  findAndBuildListFilter,
} from './list-filters';
import { composeFilterNodes } from './node-builders';
import {
  buildGraduationYearFilter,
  buildMultiGraduationYearFilter,
  buildMultiPayFilter,
  buildMultiYearsOfExperienceFilter,
  buildYearsOfExperienceFilter,
} from './range-filters';
import {
  buildMultiRoleFilter,
  buildRoleFilter,
  getRoleTaxonomyId,
} from './roles';
import { buildMultiSchoolFilter, buildSchoolFilter } from './school';
import {
  buildCustomSkillFilter,
  buildMultiCustomSkillFilter,
  getSkillTaxonomyId,
} from './skills';
import { TalentDiscoveryV3Tags } from './types';

/**
 * Build V3 talent discovery filters from Custom Dash filter state.
 * Supports all filter types: list, tree, range, free text, and special filters.
 */
export const buildTalentDiscoveryFiltersV3 = (
  filters: Filter[]
): TalentDiscoveryReqV3 => {
  // List filters (single versions - only used if MULTI versions don't exist)
  // Note: GENDER, ETHNICITY, and EDUCATION don't support current/non-current
  // Skip single filters if MULTI versions exist
  const hasMultiGender = findFilterById(filters, FilterId.MULTI_GENDER);
  const hasMultiEthnicity = findFilterById(filters, FilterId.MULTI_ETHNICITY);
  const hasMultiEducation = findFilterById(filters, FilterId.MULTI_EDUCATION);
  const hasMultiSeniority = findFilterById(filters, FilterId.MULTI_SENIORITY);

  const genderFilter = hasMultiGender
    ? null
    : findAndBuildListFilter(
        filters,
        FilterId.GENDER,
        TalentDiscoveryV3Tags.Gender,
        false
      );
  const ethnicityFilter = hasMultiEthnicity
    ? null
    : findAndBuildListFilter(
        filters,
        FilterId.ETHNICITY,
        TalentDiscoveryV3Tags.Ethnicity,
        false
      );
  const educationFilter = hasMultiEducation
    ? null
    : buildEducationFilter(filters);
  const seniorityFilter = hasMultiSeniority
    ? null
    : buildSeniorityFilter(filters);

  // Tree filters
  const companyFilter = buildCompanyFilter(filters);
  const geographyFilter = buildGeographyFilter(filters);
  const roleFilter = buildRoleFilter(filters);

  // Skill filter (single) - only used if MULTI version doesn't exist
  const hasMultiCustomSkill = findFilterById(
    filters,
    FilterId.MULTI_CUSTOM_SKILL
  );
  const customSkillFilter = hasMultiCustomSkill
    ? null
    : buildCustomSkillFilter(filters);

  // Range filters (single versions - only used if MULTI versions don't exist)
  // Skip single filters if MULTI versions exist
  const hasMultiGraduationYear = findFilterById(
    filters,
    FilterId.MULTI_GRADUATION_YEAR
  );
  const hasMultiYearsOfExperience = findFilterById(
    filters,
    FilterId.MULTI_YEARS_OF_EXPERIENCE
  );

  const graduationYearFilter = hasMultiGraduationYear
    ? null
    : buildGraduationYearFilter(filters);
  const yearsOfExperienceFilter = hasMultiYearsOfExperience
    ? null
    : buildYearsOfExperienceFilter(filters);

  // Free text filters
  const keywordsFilter = buildKeywordsFilter(filters);
  const nameFilter = buildNameFilter(filters);
  const titlesFilter = buildTitlesFilter(filters);

  // Special filters (single versions - only used if MULTI versions don't exist)
  // Skip single filters if MULTI versions exist
  const hasMultiSchool = findFilterById(filters, FilterId.MULTI_SCHOOL);
  const schoolFilter = hasMultiSchool ? null : buildSchoolFilter(filters);

  // Multi filters
  const multiCustomSkillFilter = buildMultiCustomSkillFilter(filters);
  const multiCompanyFilter = buildMultiCompanyFilter(filters);
  const multiGeographyFilter = buildMultiGeographyFilter(filters);
  const multiRoleFilter = buildMultiRoleFilter(filters);
  const multiEmploymentRelationshipFilter =
    buildMultiEmploymentRelationshipFilter(filters);
  const multiWorkScheduleFilter = buildMultiWorkScheduleFilter(filters);
  const multiFlightRiskFilter = buildMultiFlightRiskFilter(filters);
  const multiPrestigeFilter = buildMultiPrestigeFilter(filters);
  const multiRemoteSuitabilityFilter =
    buildMultiRemoteSuitabilityFilter(filters);
  const multiSeniorityFilter = buildMultiSeniorityFilter(filters);
  const multiPayFilter = buildMultiPayFilter(filters);
  const multiGenderFilter = buildMultiGenderFilter(filters);
  const multiEthnicityFilter = buildMultiEthnicityFilter(filters);
  const multiEducationFilter = buildMultiEducationFilter(filters);
  const multiGraduationYearFilter = buildMultiGraduationYearFilter(filters);
  const multiYearsOfExperienceFilter =
    buildMultiYearsOfExperienceFilter(filters);
  const multiSchoolFilter = buildMultiSchoolFilter(filters);

  const allFilterNodes = [
    // List filters
    genderFilter,
    ethnicityFilter,
    educationFilter,
    seniorityFilter,
    // Tree filters
    companyFilter,
    geographyFilter,
    roleFilter,
    customSkillFilter,
    // Range filters
    graduationYearFilter,
    yearsOfExperienceFilter,
    // Free text filters
    keywordsFilter,
    nameFilter,
    titlesFilter,
    // Special filters
    schoolFilter,
    // Multi filters
    multiCustomSkillFilter,
    multiCompanyFilter,
    multiGeographyFilter,
    multiRoleFilter,
    multiEmploymentRelationshipFilter,
    multiWorkScheduleFilter,
    multiFlightRiskFilter,
    multiPrestigeFilter,
    multiRemoteSuitabilityFilter,
    multiSeniorityFilter,
    multiPayFilter,
    multiGenderFilter,
    multiEthnicityFilter,
    multiEducationFilter,
    multiGraduationYearFilter,
    multiYearsOfExperienceFilter,
    multiSchoolFilter,
  ];

  const expr = composeFilterNodes(allFilterNodes);

  const skillsTax = getSkillTaxonomyId(filters);
  const rolesTax = getRoleTaxonomyId(filters);

  return {
    filters: {
      expr,
      roles_tax: rolesTax,
      skills_tax: skillsTax,
    },
    pagination: {
      page_num: 1,
      page_size: 5000,
    },
  };
};
